import React from "react";
import { Link } from "react-router-dom";

/**
 * Re-usable consent panel that surfaces every disclaimer link AND collects
 * granular consent flags. Used on:
 *   - /register (account creation)
 *   - Itinerary acceptance modal (clickwrap)
 *
 * `value` shape: { terms, sms, calls, marketing }
 * All flags default to false. Only `terms` is required to proceed.
 */
export default function ConsentBlock({
  value,
  onChange,
  showCallRecording = false,
  compact = false,
  testIdPrefix = "consent",
}) {
  const set = (key, checked) => onChange({ ...value, [key]: checked });

  return (
    <div
      className={`${compact ? "" : "p-4 bg-[var(--ivory-2)] border-l-2 border-[var(--gold)]"} space-y-3 text-sm text-[var(--ink)]`}
      data-testid={`${testIdPrefix}-block`}
    >
      {!compact && (
        <p className="text-xs uppercase tracking-[0.18em] text-[var(--brand-red)] mb-2">
          Please review &amp; consent
        </p>
      )}

      {/* Required — terms / privacy / disclaimers */}
      <label className="flex items-start gap-3 cursor-pointer leading-snug">
        <input
          type="checkbox"
          checked={!!value.terms}
          onChange={(e) => set("terms", e.target.checked)}
          className="mt-1 flex-shrink-0"
          required
          data-testid={`${testIdPrefix}-terms`}
        />
        <span>
          <span className="text-[var(--brand-red)]">*</span> I have read and agree to the{" "}
          <Link
            to="/legal#bookings"
            target="_blank"
            className="text-[var(--gold)] underline hover:text-[var(--emerald)]"
          >
            Booking Policy
          </Link>
          ,{" "}
          <Link
            to="/legal#privacy"
            target="_blank"
            className="text-[var(--gold)] underline hover:text-[var(--emerald)]"
          >
            Privacy Policy
          </Link>
          , and{" "}
          <Link
            to="/legal#documents"
            target="_blank"
            className="text-[var(--gold)] underline hover:text-[var(--emerald)]"
          >
            Document Control &amp; AI disclaimer
          </Link>
          .
        </span>
      </label>

      {/* Optional — SMS / text consent */}
      <label className="flex items-start gap-3 cursor-pointer leading-snug">
        <input
          type="checkbox"
          checked={!!value.sms}
          onChange={(e) => set("sms", e.target.checked)}
          className="mt-1 flex-shrink-0"
          data-testid={`${testIdPrefix}-sms`}
        />
        <span className="text-[var(--ink-2)]">
          I consent to receive trip-related SMS / text messages from Janté
          (itinerary updates, deposit confirmations, concierge follow-ups).
          Message &amp; data rates may apply · Reply STOP to opt out. See{" "}
          <Link
            to="/legal#sms"
            target="_blank"
            className="text-[var(--gold)] underline hover:text-[var(--emerald)]"
          >
            SMS consent policy
          </Link>
          .
        </span>
      </label>

      {/* Optional — recorded calls — only on flows where the next step is a call */}
      {showCallRecording && (
        <label className="flex items-start gap-3 cursor-pointer leading-snug">
          <input
            type="checkbox"
            checked={!!value.calls}
            onChange={(e) => set("calls", e.target.checked)}
            className="mt-1 flex-shrink-0"
            data-testid={`${testIdPrefix}-calls`}
          />
          <span className="text-[var(--ink-2)]">
            I consent to recording of calls placed to or received from Janté
            for quality, training, and itinerary-confirmation purposes. See{" "}
            <Link
              to="/legal#calls"
              target="_blank"
              className="text-[var(--gold)] underline hover:text-[var(--emerald)]"
            >
              Recorded-call policy
            </Link>
            .
          </span>
        </label>
      )}

      {/* Optional — marketing emails */}
      <label className="flex items-start gap-3 cursor-pointer leading-snug">
        <input
          type="checkbox"
          checked={!!value.marketing}
          onChange={(e) => set("marketing", e.target.checked)}
          className="mt-1 flex-shrink-0"
          data-testid={`${testIdPrefix}-marketing`}
        />
        <span className="text-[var(--ink-2)]">
          Send me curated Janté travel offers and Sasha's seasonal picks. You
          can unsubscribe any time from any email.
        </span>
      </label>

      {!compact && (
        <p className="text-[10px] uppercase tracking-[0.16em] text-[var(--muted)] mt-3">
          <span className="text-[var(--brand-red)]">*</span> Required
        </p>
      )}
    </div>
  );
}
